import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  ChevronIcon,
  DisturbingCard,
  DropdownButton,
  DropdownContainer,
  DropdownItem,
  DropdownMenu,
  ScrollIcon,
} from "./Dropdown.styles";

import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { ReactComponent as Checked } from "../../assets/icons/check01.svg";
import { ReactComponent as WhiteLock } from "../../assets/icons/whiteLock.svg";
import { ReactComponent as CheckedIcon } from "../../assets/icons/checked.svg";
import { ReactComponent as UncheckedIcon } from "../../assets/icons/unchecked.svg";
import { toast } from "../modern-ui/sonner";
import scrollAnimation from "../../assets/animations/swipeUp.json";
import LottieIcon from "../LottieIcon/LottieIcon";
const Dropdown = ({
  options = [],
  placeholder = "All",
  onChange,
  defaultValue,
  isProEnabled,
  fullWidth = false,
  multipleSelect = false,
  min,
  max,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(
    multipleSelect ? defaultValue || [] : defaultValue,
  );
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const [isPositionReady, setIsPositionReady] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useLockBodyScroll(isOpen);

  useEffect(() => {
    updatePosition();
  }, []);

  useEffect(() => {
    if (!defaultValue) {
      setSelectedValue(multipleSelect ? [] : null);
    }
  }, [defaultValue, multipleSelect]);

  useEffect(() => {
    if (defaultValue && !multipleSelect) {
      setSelectedValue(defaultValue);
    }
  }, [defaultValue, multipleSelect]);

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 7,
        left: rect.left,
        width: rect.width,
      });

      setIsPositionReady(true);
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      setHasScrolled(false);
      setAnimationKey((prev) => prev + 1);
    } else {
      setIsPositionReady(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleMenuScroll = () => {
      setHasScrolled(true);
    };

    if (isOpen && menuRef.current) {
      menuRef.current.addEventListener("scroll", handleMenuScroll);

      return () => {
        if (menuRef.current) {
          // eslint-disable-next-line
          menuRef.current.removeEventListener("scroll", handleMenuScroll);
        }
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    if (multipleSelect) {
      const isCurrentlySelected = selectedValue.includes(option);

      if (isCurrentlySelected) {
        const newValue = selectedValue.filter((item) => item !== option);

        if (min && newValue.length < min) {
          toast.error(
            `You must select at least ${min} ${
              min === 1 ? "category" : "categories"
            }`,
          );
          return;
        }

        setSelectedValue(newValue);
        if (onChange) {
          onChange(newValue);
        }
      } else {
        if (max && selectedValue.length >= max) {
          toast.error(
            `You can select maximum ${max} ${
              max === 1 ? "category" : "categories"
            }`,
          );
          return;
        }

        const newValue = [...selectedValue, option];
        setSelectedValue(newValue);
        if (onChange) {
          onChange(newValue);
        }
      }
    } else {
      setSelectedValue(option);
      setIsOpen(false);
      if (onChange) {
        onChange(option);
      }
    }
  };

  const getDisplayText = () => {
    if (multipleSelect) {
      if (selectedValue.length === 0) {
        return placeholder;
      }
      return selectedValue.join(", ");
    }
    return selectedValue || placeholder;
  };

  const isSelected = (optionLabel) => {
    if (multipleSelect) {
      return selectedValue.includes(optionLabel);
    }
    return optionLabel === selectedValue;
  };

  const canSelect = (optionLabel) => {
    if (!multipleSelect) return true;

    const isCurrentlySelected = selectedValue.includes(optionLabel);

    if (isCurrentlySelected) {
      return !min || selectedValue.length > min;
    }

    return !max || selectedValue.length < max;
  };

  return (
    <>
      <DropdownContainer $fullWidth={fullWidth}>
        <DropdownButton
          $multipleSelect={multipleSelect}
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          $fullWidth={fullWidth}
        >
          <span>{getDisplayText()}</span>
          <ChevronIcon $isOpen={isOpen} />
        </DropdownButton>
      </DropdownContainer>

      {createPortal(
        <DropdownMenu
          ref={menuRef}
          $isOpen={isOpen && isPositionReady}
          $top={position.top}
          $left={position.left}
          $width={position.width}
        >
          {!multipleSelect && (
            <DropdownItem $noClick $active>
              Filter By
            </DropdownItem>
          )}
          {options.map((option, index) => {
            const isDisturbing = option.label === "Bizarre";
            const optionLabel = option?.label ?? option;
            const selected = isSelected(optionLabel);
            const disabled = isDisturbing && !isProEnabled;

            return (
              <DropdownItem
                key={index}
                $active={selected}
                $noClick={disabled}
                $disabled={!canSelect(optionLabel) && multipleSelect}
                onClick={(e) => {
                  if (disabled) {
                    e.stopPropagation();
                  } else {
                    handleSelect(optionLabel);
                  }
                }}
              >
                {optionLabel}

                {!isDisturbing && multipleSelect ? (
                  selected ? (
                    <CheckedIcon width={18} height={18} />
                  ) : (
                    <UncheckedIcon width={18} height={18} />
                  )
                ) : (
                  selected && <Checked width={14} height={14} />
                )}

                {isDisturbing && (
                  <DisturbingCard>
                    <WhiteLock height={14} width={14}></WhiteLock>PRO
                  </DisturbingCard>
                )}
              </DropdownItem>
            );
          })}
          {isOpen && !hasScrolled && (
            <ScrollIcon>
              <LottieIcon
                key={animationKey}
                animationData={scrollAnimation}
                autoplay={true}
                loop={1}
                size={80}
              />
            </ScrollIcon>
          )}
        </DropdownMenu>,
        document.body,
      )}
    </>
  );
};

export default Dropdown;
